import React, { useState } from 'react';
import { TierBadge, Footer, MonoLabel } from './ui';
import { loadLang, saveLang } from '../data/translations';
import { ESSAY_TYPES } from '../data/prompts';
import { ME_PROMPTS, ME_COUNTRIES } from '../data/middleEastPrompts';
import { canWriteSmart, canWriteTest, remainingSmart, remainingTest, isTrial } from '../data/storage';
import { TOPIC_IDEAS, SECTION_COLORS } from '../data/topicIdeas';
import { loadGeneratedIdeas } from '../data/generatedIdeas';
import IdeaPractice from './IdeaPractice';
import SentencePractice from './SentencePractice';
import VocabularyBank from './VocabularyBank';
import AnnouncementBanner from './AnnouncementBanner';
import WelcomeModal from './WelcomeModal';

export default function HomeScreen({ branding, candidate, tiers, prompts, onSelectPrompt, onSelectTestMode, onLogout, onAdmin, onResults, onEssays, onDashboard, onPlacementTest, onCourses, onAcademy, onShowLead, onBack }) {
  const [activeTab,     setActiveTab]     = useState('smart');
  const [lang,          setLang]          = useState(() => loadLang());
  const [meCountry,     setMeCountry]     = useState('All');
  const [meType,        setMeType]        = useState('All');
  const [expandedIdeas,  setExpandedIdeas]  = useState({});
  const [generatedIdeas] = useState(() => loadGeneratedIdeas());

  const tier      = tiers[candidate.tier] || tiers.trial;
  const trial     = isTrial(candidate);
  const remSmart  = remainingSmart(candidate, tiers);
  const remTest   = remainingTest(candidate, tiers);

  const filteredMe = ME_PROMPTS.filter(p =>
    (meCountry === 'All' || p.country === meCountry) &&
    (meType    === 'All' || p.type    === meType)
  );

  const NAV_SVG = {
    smart: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>,
    test:  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
    me:    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>,
    ideas: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="9" y1="18" x2="15" y2="18"/><line x1="10" y1="22" x2="14" y2="22"/><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0018 8 6 6 0 006 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 018.91 14"/></svg>,
    sentences: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="17" y1="10" x2="3" y2="10"/><line x1="21" y1="6" x2="3" y2="6"/><line x1="21" y1="14" x2="3" y2="14"/><line x1="17" y1="18" x2="3" y2="18"/></svg>,
    vocab: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/></svg>,
  };
  const NAV_CARDS = [
    { id:'smart',     title:'Smart Writing', subtitle:'Guided practice',    color:'#1a2a4a' },
    { id:'test',      title:'Test Mode',     subtitle:'Exam simulation',    color:'#b8620a' },
    { id:'me',        title:'Middle East',   subtitle:'Regional questions', color:'#0a6a7a' },
    { id:'ideas',     title:'Ideas',         subtitle:'Topic idea banks',   color:'#b8860b' },
    { id:'sentences', title:'Sentences',     subtitle:'Structure practice', color:'#1a5fa8' },
    { id:'vocab',     title:'Vocabulary',    subtitle:'Words & practice',   color:'#1a7a4a' },
  ];

  // For trial: only "Discuss Both Views" is available, and only 1 attempt
  function isSmartLocked(et) {
    if (trial) return !et.trialOk; // only discuss is trialOk
    return false;
  }

  function canStartSmart(p, et) {
    if (isSmartLocked(et)) return false;
    return canWriteSmart(candidate, tiers);
  }

  function canStartTest() {
    return canWriteTest(candidate, tiers);
  }

  function handleSmartClick(p, et) {
    if (!canStartSmart(p, et)) { onShowLead && onShowLead('smart'); return; }
    onSelectPrompt(p, et);
  }

  function handleTestClick(p, et) {
    if (!canStartTest()) { onShowLead && onShowLead('test'); return; }
    onSelectTestMode(p, et);
  }

  const smartCard = (p, et, i) => {
    const locked = isSmartLocked(et);
    const ok     = canStartSmart(p, et);
    const isUsed = trial && (candidate.usedSmart||0) >= 1 && et.trialOk;
    return (
      <div key={p.id}
        onClick={() => handleSmartClick(p, et)}
        style={{ background:'#ffffff', border:`1.5px solid ${locked||isUsed?'#e8e8e8':'#dde3ed'}`, borderRadius:12, padding:'18px 20px', cursor:(locked||isUsed)?'default':'pointer', opacity:(locked||isUsed)?.5:1, transition:'all .2s', boxShadow:'0 1px 4px rgba(26,42,74,0.06)' }}
        onMouseEnter={e=>{ if(!locked&&!isUsed){ e.currentTarget.style.borderColor=et.accent; e.currentTarget.style.boxShadow='0 4px 16px rgba(26,42,74,0.12)'; e.currentTarget.style.transform='translateY(-2px)'; }}}
        onMouseLeave={e=>{ e.currentTarget.style.borderColor=(locked||isUsed)?'#e8e8e8':'#dde3ed'; e.currentTarget.style.boxShadow='0 1px 4px rgba(26,42,74,0.06)'; e.currentTarget.style.transform='translateY(0)'; }}>
        <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:9, letterSpacing:'.12em', textTransform:'uppercase', color:locked?'#c0c0c0':et.accent, marginBottom:6, fontWeight:600 }}>
          {p.year||`Prompt ${i+1}`}
        </div>
        <div style={{ fontSize:15, fontWeight:600, color:locked?'#aaaaaa':'#1a2233', marginBottom:7, lineHeight:1.3 }}>{p.title}</div>
        <div style={{ fontSize:12, color:locked?'#bbbbbb':'#5a6a88', lineHeight:1.65, fontStyle:'italic' }}>
          {p.text.length>100?p.text.slice(0,100)+'…':p.text}
        </div>
        <div style={{ marginTop:12, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          {locked && <span style={{ fontSize:11, color:'#aaaaaa' }}>🔒 {trial?'Upgrade to access':'Locked'}</span>}
          {isUsed && <span style={{ fontSize:11, color:'#aaaaaa' }}>✓ Trial used — contact Mr. Ahmed for more</span>}
          {!locked && !isUsed && ok && <span style={{ fontSize:12.5, fontWeight:600, color:et.accent }}>Start Writing →</span>}
          {!locked && !isUsed && !ok && <span style={{ fontSize:11, color:'#b8620a', fontWeight:500 }}>📲 Contact Mr. Ahmed to continue</span>}
        </div>
        {/* Expandable ideas preview — static bank OR AI-generated bank */}
        {(() => {
          const td = (p.ideaKey && TOPIC_IDEAS[p.ideaKey]) || generatedIdeas[String(p.id)];
          if (!td) return null;
          const eKey = `${et.id}_${p.id}`;
          const isOpen = expandedIdeas[eKey];
          return (
            <div style={{ marginTop:10 }}>
              <button onClick={e => { e.stopPropagation(); setExpandedIdeas(x => ({...x,[eKey]:!x[eKey]})); }}
                style={{ background:'none', border:'none', color:'#b8860b', fontSize:11.5, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:500, padding:0, display:'flex', alignItems:'center', gap:4 }}>
                💡 {isOpen ? 'Hide Ideas' : 'Quick Ideas Preview'}
              </button>
              {isOpen && (
                <div style={{ marginTop:8, background:'#fafafa', border:'1px solid #e8e8e8', borderRadius:7, padding:'11px 13px' }} onClick={e=>e.stopPropagation()}>
                  {td.sections.slice(0,2).map((sec,si) => {
                    const cfg = SECTION_COLORS[sec.color] || SECTION_COLORS.teal;
                    return (
                      <div key={si} style={{ marginBottom: si<1 ? 9 : 0 }}>
                        <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:8.5, letterSpacing:'.1em', textTransform:'uppercase', color:cfg.label, marginBottom:5 }}>{sec.label}</div>
                        {sec.items.slice(0,2).map((item,ii) => (
                          <div key={ii} style={{ fontSize:11.5, color:cfg.text, lineHeight:1.55, padding:'3px 0 3px 10px', borderLeft:`2px solid ${cfg.border}`, marginBottom:3 }}>{item}</div>
                        ))}
                      </div>
                    );
                  })}
                  {td.sections.find(s=>s.color==='purple') && (
                    <div style={{ marginTop:9, display:'flex', gap:5, flexWrap:'wrap' }}>
                      {td.sections.find(s=>s.color==='purple').items.slice(0,5).map((w,wi) => (
                        <span key={wi} style={{ fontSize:10.5, background:'#ede9fe', color:'#5b21b6', border:'1px solid #c4b5fd', borderRadius:100, padding:'1px 8px' }}>{w}</span>
                      ))}
                    </div>
                  )}
                  <button onClick={()=>setActiveTab('ideas')} style={{ marginTop:10, fontSize:11, color:'#1a2a4a', background:'#f0f4f8', border:'1px solid #dde3ed', borderRadius:6, padding:'5px 12px', cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                    Open full idea bank &rarr;
                  </button>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    );
  };

  const testCard = (p, et, i) => {
    const ok     = canStartTest();
    const isUsed = trial && (candidate.usedTest||0) >= 1;
    return (
      <div key={p.id}
        onClick={() => handleTestClick(p, et)}
        style={{ background:'#ffffff', border:`1.5px solid ${isUsed?'#e8e8e8':'#dde3ed'}`, borderRadius:12, padding:'18px 20px', cursor:isUsed?'default':'pointer', opacity:isUsed?.5:1, transition:'all .2s', boxShadow:'0 1px 4px rgba(26,42,74,0.06)' }}
        onMouseEnter={e=>{ if(!isUsed){ e.currentTarget.style.borderColor='#b8620a'; e.currentTarget.style.boxShadow='0 4px 16px rgba(26,42,74,0.12)'; e.currentTarget.style.transform='translateY(-2px)'; }}}
        onMouseLeave={e=>{ e.currentTarget.style.borderColor=isUsed?'#e8e8e8':'#dde3ed'; e.currentTarget.style.boxShadow='0 1px 4px rgba(26,42,74,0.06)'; e.currentTarget.style.transform='translateY(0)'; }}>
        {p.country && <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:9, letterSpacing:'.1em', textTransform:'uppercase', color:isUsed?'#aaa':'#25d366', marginBottom:5, fontWeight:600 }}>📍 {p.country}{p.city&&p.city!=='General'?' · '+p.city:''} · {p.date}</div>}
        {!p.country && <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:9, letterSpacing:'.1em', textTransform:'uppercase', color:isUsed?'#aaa':et.accent, marginBottom:5, fontWeight:600 }}>{p.year||`Prompt ${i+1}`}</div>}
        <div style={{ fontSize:15, fontWeight:600, color:isUsed?'#aaaaaa':'#1a2233', marginBottom:7, lineHeight:1.3 }}>{p.title}</div>
        <div style={{ fontSize:12, color:isUsed?'#bbbbbb':'#5a6a88', lineHeight:1.65, fontStyle:'italic' }}>{p.text.length>100?p.text.slice(0,100)+'…':p.text}</div>
        <div style={{ marginTop:12, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          {isUsed && <span style={{ fontSize:11, color:'#aaaaaa' }}>✓ Trial used — contact Mr. Ahmed for more</span>}
          {!isUsed && ok && (
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              <span style={{ fontSize:10, fontFamily:"'JetBrains Mono',monospace", background:'#1a2a4a', color:'#ffffff', padding:'2px 8px', borderRadius:100 }}>40 MIN TIMER</span>
              <span style={{ fontSize:12.5, fontWeight:600, color:'#b8620a' }}>Start Test →</span>
            </div>
          )}
          {!isUsed && !ok && <span style={{ fontSize:11, color:'#b8620a', fontWeight:500 }}>📲 Contact Mr. Ahmed to continue</span>}
        </div>
      </div>
    );
  };

  return (
    <div style={{ minHeight:'100vh', background:'#f0f4f8', display:'flex', flexDirection:'column' }}>

      {/* First-time welcome modal */}
      <WelcomeModal candidate={candidate} onClose={()=>{}}/>

      {/* NAV */}
      <div style={{ background:'#ffffff', borderBottom:'1px solid #dde3ed', padding:'12px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', boxShadow:'0 1px 4px rgba(0,0,0,0.06)', position:'sticky', top:0, zIndex:200 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <img src="/logo.png" alt="IELTS Compass" style={{ height:38, objectFit:'contain', borderRadius:6, flexShrink:0, cursor:'pointer' }} onClick={()=>window.scrollTo({top:0,behavior:'smooth'})}/>
          <div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:26, fontWeight:700, color:'#1a2a4a', lineHeight:1.05 }}>
              IELTS<span style={{color:'#b8860b'}}>COMPASS</span>
              <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13, fontWeight:500, color:'#5a6a88', marginLeft:8 }}>IELTS with {branding?.tagline?.replace('with ','')||'Mr. Ahmed Salem'}</span>
            </div>
            <div style={{ fontSize:12, color:'#b8860b', fontStyle:'italic', lineHeight:1, marginBottom:2 }}>{branding?.motto||'Navigate your path to success.'}</div>
            <div style={{ display:'flex', alignItems:'center', gap:12, marginTop:2 }}>
              {[branding?.phone1||'00201008182425', branding?.phone2||'00971565727742'].map(p=>(
                <a key={p} href={`https://wa.me/${p.replace(/\D/g,'')}`} target="_blank" rel="noreferrer"
                  style={{ display:'flex', alignItems:'center', gap:4, fontSize:11, color:'#25d366', fontFamily:"'JetBrains Mono',monospace", textDecoration:'none' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="#25d366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.522 3.66 1.438 5.168L2 22l4.978-1.301A9.96 9.96 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18c-1.72 0-3.328-.463-4.717-1.268l-.338-.201-3.508.917.936-3.417-.221-.352A7.944 7.944 0 014 12c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"/></svg>
                  {p}
                </a>
              ))}
            </div>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', justifyContent:'flex-end' }}>
          {onBack && (
            <button onClick={onBack}
              style={{ background:'#f0f4f8', border:'1px solid #c0ccd8', color:'#1a2a4a', padding:'6px 13px', borderRadius:7, cursor:'pointer', fontSize:12, fontWeight:600, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
              ← My Dashboard
            </button>
          )}
          <TierBadge tier={candidate.tier}/>
          {!trial && (
            <div style={{ display:'flex', gap:5 }}>
              <span style={{ fontSize:11, color:'#1a5fa8', background:'#e8f0fb', padding:'4px 8px', borderRadius:100, border:'1px solid #c0d4f0' }}>✍️ {remSmart}</span>
              <span style={{ fontSize:11, color:'#b8620a', background:'#fff8f0', padding:'4px 8px', borderRadius:100, border:'1px solid #fde8cc' }}>⏱ {remTest}</span>
            </div>
          )}
          {trial && <span style={{ fontSize:11, color:'#b8620a', background:'#fff8f0', padding:'4px 10px', borderRadius:100, border:'1px solid #fde8cc', cursor:'pointer' }} onClick={()=>onShowLead&&onShowLead('smart')}>Free trial ↗</span>}
          <button onClick={onDashboard} style={{ background:'#f7f9fc', border:'1px solid #dde3ed', color:'#5a6a88', padding:'6px 10px', borderRadius:7, cursor:'pointer', fontSize:12 }} title="My Writing Progress Tracker">📈</button>
          <button onClick={onAcademy} style={{ background:'#b8860b', border:'none', color:'#fff', padding:'6px 12px', borderRadius:7, cursor:'pointer', fontSize:12, fontWeight:600 }}>🏫 Academy</button>
          <button onClick={onCourses} style={{ background:'#f7f9fc', border:'1px solid #dde3ed', color:'#5a6a88', padding:'6px 10px', borderRadius:7, cursor:'pointer', fontSize:12 }}>📚</button>
          {onAdmin && <button onClick={onAdmin} style={{ background:'#1a2a4a', border:'none', color:'#ffffff', padding:'6px 12px', borderRadius:7, cursor:'pointer', fontSize:12, fontWeight:500 }}>⚙ Admin</button>}
          <button onClick={onLogout} style={{ background:'transparent', border:'1px solid #dde3ed', color:'#5a6a88', padding:'6px 12px', borderRadius:7, cursor:'pointer', fontSize:12 }}>Sign Out</button>
        </div>
      </div>

      {/* HERO */}
      <div style={{ background:'linear-gradient(135deg,#1a2a4a 0%,#2a3a6a 50%,#1a2a4a 100%)', padding:'36px 24px 28px', textAlign:'center', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, background:'radial-gradient(ellipse at 50% 0%,rgba(184,134,11,0.15) 0%,transparent 60%)', pointerEvents:'none' }}/>
        <div style={{ position:'relative' }}>
          <MonoLabel color='#b8860b' style={{ marginBottom:10 }}>IELTS Writing Task 2 · AI Coaching Platform</MonoLabel>
          <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'clamp(24px,4vw,42px)', fontWeight:700, lineHeight:1.15, marginBottom:10, color:'#ffffff' }}>
            Welcome, <em style={{ color:'#d4a017', fontStyle:'italic' }}>{candidate.name||candidate.email.split('@')[0]}</em>
          </h1>
          <p style={{ fontSize:13.5, color:'rgba(255,255,255,0.65)', fontWeight:300, maxWidth:520, margin:'0 auto', lineHeight:1.7 }}>
            {trial
              ? 'Free trial — 1 Smart Writing + 1 Test Mode practice included. Upgrade for full access.'
              : 'Sentence-by-sentence guidance · Idea banks · AI feedback · Exam simulation'}
          </p>
        </div>
      </div>

      {/* TRIAL BANNER */}
      {trial && (
        <div style={{ background:'#fff8f0', borderBottom:'1px solid #fde8cc', padding:'12px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
          <div style={{ fontSize:13, color:'#7a4a00' }}>
            <strong>Trial access:</strong> 1 Smart Writing (Discuss Both Views) · 1 Test Mode · Immediate AI feedback on both
          </div>
          <button onClick={()=>onShowLead&&onShowLead('smart')}
            style={{ background:'#25d366', color:'#ffffff', border:'none', padding:'7px 16px', borderRadius:7, fontSize:12.5, fontWeight:600, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
            📲 Upgrade via WhatsApp
          </button>
        </div>
      )}

      {/* ANNOUNCEMENT BANNER */}
      <AnnouncementBanner candidate={candidate}/>

      {/* NAV CARDS — grouped */}
      <div style={{ background:'#ffffff', borderBottom:'1px solid #e2e6ed', boxShadow:'0 2px 6px rgba(26,42,74,0.06)', position:'sticky', top:0, zIndex:100 }}>
        <div style={{ maxWidth:1000, margin:'0 auto', padding:'12px 18px 14px' }}>
          {/* Writing Practice group */}
          <div style={{ marginBottom:10 }}>
            <div style={{ display:'inline-flex', alignItems:'center', gap:5, background:'#1a2a4a', color:'#fff', borderRadius:100, padding:'2px 10px', fontSize:10.5, fontWeight:600, letterSpacing:'.05em', marginBottom:8, fontFamily:"'JetBrains Mono',monospace" }}>
              ✍️ Writing Practice
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
              {NAV_CARDS.slice(0,3).map(c => {
                const active = activeTab === c.id;
                return (
                  <button key={c.id} onClick={() => setActiveTab(c.id)}
                    onMouseEnter={e=>{ if(!active){e.currentTarget.style.borderColor='#1a2a4a44';e.currentTarget.style.transform='translateY(-1px)';} }}
                    onMouseLeave={e=>{ if(!active){e.currentTarget.style.borderColor='#e2e6ed';e.currentTarget.style.transform='none';} }}
                    style={{ border:`1.5px solid ${active?'#1a2a4a':'#e2e6ed'}`, background:active?'#f0f4f8':'#fff', borderRadius:10, padding:'12px 8px', textAlign:'center', cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", boxShadow:active?'0 2px 8px rgba(26,42,74,0.12)':'none', transition:'all .15s', outline:'none' }}>
                    <div style={{ display:'flex', justifyContent:'center', marginBottom:5, color:active?'#1a2a4a':c.color }}>{NAV_SVG[c.id]}</div>
                    <div style={{ fontSize:12.5, fontWeight:600, color:active?'#1a2a4a':'#1a2233', lineHeight:1.2, marginBottom:2 }}>{c.title}</div>
                    <div style={{ fontSize:10.5, color:active?'#5a6a88':'#9aa3b8', lineHeight:1.2 }}>{c.subtitle}</div>
                  </button>
                );
              })}
            </div>
          </div>
          {/* Study Tools group */}
          <div>
            <div style={{ display:'inline-flex', alignItems:'center', gap:5, background:'#b8860b', color:'#fff', borderRadius:100, padding:'2px 10px', fontSize:10.5, fontWeight:600, letterSpacing:'.05em', marginBottom:8, fontFamily:"'JetBrains Mono',monospace" }}>
              📚 Study Tools
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
              {NAV_CARDS.slice(3).map(c => {
                const active = activeTab === c.id;
                return (
                  <button key={c.id} onClick={() => setActiveTab(c.id)}
                    onMouseEnter={e=>{ if(!active){e.currentTarget.style.borderColor='#b8860b44';e.currentTarget.style.transform='translateY(-1px)';} }}
                    onMouseLeave={e=>{ if(!active){e.currentTarget.style.borderColor='#e2e6ed';e.currentTarget.style.transform='none';} }}
                    style={{ border:`1.5px solid ${active?'#b8860b':'#e2e6ed'}`, background:active?'#fffbf0':'#fff', borderRadius:10, padding:'12px 8px', textAlign:'center', cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", boxShadow:active?'0 2px 8px rgba(184,134,11,0.15)':'none', transition:'all .15s', outline:'none' }}>
                    <div style={{ display:'flex', justifyContent:'center', marginBottom:5, color:active?'#b8860b':c.color }}>{NAV_SVG[c.id]}</div>
                    <div style={{ fontSize:12.5, fontWeight:600, color:active?'#b8860b':'#1a2233', lineHeight:1.2, marginBottom:2 }}>{c.title}</div>
                    <div style={{ fontSize:10.5, color:active?'#b8860b':'#9aa3b8', lineHeight:1.2 }}>{c.subtitle}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* CONTENT */}
      <div style={{ maxWidth:1000, margin:'0 auto', padding:'24px 18px', flex:1, width:'100%' }}>

        {/* Mode banners */}
        {activeTab==='smart' && (
          <div style={{ background:'#e8f0fb', border:'1px solid #c0d4f0', borderRadius:9, padding:'12px 16px', marginBottom:20, display:'flex', gap:12, alignItems:'flex-start' }}>
            <div style={{ fontSize:20, flexShrink:0 }}>✍️</div>
            <div>
              <div style={{ fontSize:13.5, fontWeight:600, color:'#1a5fa8', marginBottom:3 }}>Smart Writing Mode — Guided Practice</div>
              <div style={{ fontSize:12.5, color:'#5a6a88', lineHeight:1.6 }}>
                Write sentence by sentence with formula guidance, idea banks, and AI feedback on each paragraph.
                {trial ? ' Trial includes 1 free attempt on "Discuss Both Views". Standard and Premium unlock all 5 essay types.' : ' All essay types unlocked. AI feedback included.'}
              </div>
            </div>
          </div>
        )}
        {activeTab==='test' && (
          <div style={{ background:'#fff8f0', border:'1px solid #fde8cc', borderRadius:9, padding:'12px 16px', marginBottom:20, display:'flex', gap:12, alignItems:'flex-start' }}>
            <div style={{ fontSize:20, flexShrink:0 }}>⏱</div>
            <div>
              <div style={{ fontSize:13.5, fontWeight:600, color:'#b8620a', marginBottom:3 }}>Test Mode — Real Exam Simulation</div>
              <div style={{ fontSize:12.5, color:'#5a6a88', lineHeight:1.6 }}>
                40-minute timer. No formulas or hints — just you and the question. AI scores across all 4 criteria (TA, CC, LR, GRA).
                {trial ? ' Trial includes 1 free test. Standard and Premium have unlimited Test Mode.' : ' Unlimited attempts on all questions.'}
              </div>
            </div>
          </div>
        )}

        {/* SMART WRITING */}
        {activeTab==='smart' && ESSAY_TYPES.map((et, ti) => {
          const sectionLocked = isSmartLocked(et);
          return (
            <div key={et.id} className="fade-up" style={{ marginBottom:28, animationDelay:`${ti*.05}s` }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
                <div style={{ width:32, height:32, borderRadius:7, background:sectionLocked?'#f0f4f8':'#1a2a4a', border:`1.5px solid ${sectionLocked?'#dde3ed':et.accent}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, color:sectionLocked?'#9aa3b8':et.accent, fontWeight:700 }}>{et.icon}</div>
                <div style={{ fontSize:15, fontWeight:600, color:sectionLocked?'#9aa3b8':'#1a2233' }}>{et.label}</div>
                {sectionLocked && (
                  <span style={{ fontSize:11, background:'#f7f9fc', color:'#9aa3b8', padding:'3px 10px', borderRadius:100, border:'1px solid #dde3ed' }}>
                    {trial ? '🔒 Standard / Premium only' : '🔒 Locked'}
                  </span>
                )}
                <div style={{ flex:1, height:'1px', background:'#dde3ed' }}/>
              </div>
              {sectionLocked ? (
                <div style={{ background:'#f7f9fc', border:'1px solid #dde3ed', borderRadius:9, padding:'14px', textAlign:'center', fontSize:13, color:'#9aa3b8' }}>
                  Available on Standard and Premium plans.{' '}
                  <button onClick={()=>onShowLead&&onShowLead('smart')} style={{ background:'none', border:'none', color:'#25d366', cursor:'pointer', fontSize:13, fontWeight:600, textDecoration:'underline' }}>Contact Mr. Ahmed to upgrade →</button>
                </div>
              ) : (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(270px,1fr))', gap:12 }}>
                  {(prompts[et.id]||[]).map((p,i) => smartCard(p, et, i))}
                </div>
              )}
            </div>
          );
        })}

        {/* TEST MODE */}
        {activeTab==='test' && ESSAY_TYPES.map((et, ti) => (
          <div key={et.id} style={{ marginBottom:28 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
              <div style={{ width:32, height:32, borderRadius:7, background:'#1a2a4a', border:`1.5px solid ${et.accent}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, color:et.accent }}>{et.icon}</div>
              <div style={{ fontSize:15, fontWeight:600, color:'#1a2233' }}>{et.label}</div>
              <div style={{ flex:1, height:'1px', background:'#dde3ed' }}/>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(270px,1fr))', gap:12 }}>
              {(prompts[et.id]||[]).map((p,i) => testCard(p, et, i))}
            </div>
          </div>
        ))}

        {/* MIDDLE EAST */}
        {activeTab==='me' && (
          <div>
            <div style={{ background:'#e8f5ee', border:'1px solid #c8ead8', borderRadius:9, padding:'12px 16px', marginBottom:20, display:'flex', gap:12, alignItems:'flex-start' }}>
              <div style={{ fontSize:20, flexShrink:0 }}>📍</div>
              <div>
                <div style={{ fontSize:13.5, fontWeight:600, color:'#1a7a4a', marginBottom:3 }}>Middle East Question Bank</div>
                <div style={{ fontSize:12.5, color:'#5a6a88', lineHeight:1.6 }}>Real IELTS questions from UAE, Egypt, Qatar, Saudi Arabia (2025–2026). Filter by country. Use both Smart Writing and Test Mode on any question.</div>
              </div>
            </div>
            <div style={{ display:'flex', gap:10, marginBottom:18, flexWrap:'wrap' }}>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                {ME_COUNTRIES.map(c=>(
                  <button key={c} onClick={()=>setMeCountry(c)} style={{ padding:'5px 12px', borderRadius:100, border:`1.5px solid ${meCountry===c?'#b8860b':'#dde3ed'}`, background:meCountry===c?'#b8860b':'#ffffff', color:meCountry===c?'#ffffff':'#5a6a88', cursor:'pointer', fontSize:12, fontWeight:meCountry===c?600:400, transition:'all .15s' }}>{c}</button>
                ))}
              </div>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                {['All','discuss','opinion','advantages','problems','twoquestions'].map(t=>(
                  <button key={t} onClick={()=>setMeType(t)} style={{ padding:'5px 12px', borderRadius:100, border:`1.5px solid ${meType===t?'#1a5fa8':'#dde3ed'}`, background:meType===t?'#1a5fa8':'#ffffff', color:meType===t?'#ffffff':'#5a6a88', cursor:'pointer', fontSize:12, transition:'all .15s' }}>{t==='All'?'All Types':t}</button>
                ))}
              </div>
            </div>
            <div style={{ fontSize:12, color:'#9aa3b8', marginBottom:14 }}>{filteredMe.length} question{filteredMe.length!==1?'s':''} found</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:14 }}>
              {filteredMe.map((p) => {
                const et   = ESSAY_TYPES.find(t=>t.id===p.type)||ESSAY_TYPES[0];
                const okS  = canWriteSmart(candidate, tiers);
                const okT  = canWriteTest(candidate, tiers);
                return (
                  <div key={p.id} style={{ background:'#ffffff', border:'1.5px solid #dde3ed', borderRadius:12, padding:'16px 18px', boxShadow:'0 1px 4px rgba(26,42,74,0.06)' }}>
                    <div style={{ display:'flex', gap:6, marginBottom:8, flexWrap:'wrap' }}>
                      <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:9, background:'#e8f5ee', color:'#1a7a4a', padding:'2px 8px', borderRadius:100, fontWeight:600 }}>📍 {p.country}{p.city&&p.city!=='General'?' · '+p.city:''}</span>
                      <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:9, background:'#f7f9fc', color:'#5a6a88', padding:'2px 8px', borderRadius:100 }}>{p.date}</span>
                      <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:9, background:et.color+'22', color:et.accent, padding:'2px 8px', borderRadius:100 }}>{et.icon} {p.type}</span>
                    </div>
                    <div style={{ fontSize:14.5, fontWeight:600, color:'#1a2233', marginBottom:7, lineHeight:1.3 }}>{p.title}</div>
                    <div style={{ fontSize:12, color:'#5a6a88', lineHeight:1.65, fontStyle:'italic', marginBottom:12 }}>{p.text.length>90?p.text.slice(0,90)+'…':p.text}</div>
                    <div style={{ display:'flex', gap:7 }}>
                      <button onClick={()=>okS?onSelectPrompt(p,et):onShowLead('smart')}
                        style={{ flex:1, background:okS?'#1a2a4a':'#f0f4f8', color:okS?'#ffffff':'#9aa3b8', border:'none', padding:'8px', borderRadius:7, fontSize:12, fontWeight:500, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                        ✍️ Smart Writing
                      </button>
                      <button onClick={()=>okT?onSelectTestMode(p,et):onShowLead('test')}
                        style={{ flex:1, background:okT?'#b8860b':'#f0f4f8', color:okT?'#ffffff':'#9aa3b8', border:'none', padding:'8px', borderRadius:7, fontSize:12, fontWeight:500, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                        ⏱ Test Mode
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* IDEAS PRACTICE TAB */}
        {activeTab==='ideas' && (
          <IdeaPractice branding={branding} candidate={candidate} tiers={tiers}/>
        )}

        {/* SENTENCE PRACTICE TAB */}
        {activeTab==='sentences' && (
          <SentencePractice candidate={candidate}/>
        )}

        {/* VOCABULARY BANK TAB */}
        {activeTab==='vocab' && (
          <VocabularyBank candidate={candidate}/>
        )}

        {activeTab!=='ideas' && activeTab!=='sentences' && activeTab!=='vocab' && (
          <div style={{ display:'flex', gap:10, marginTop:20, flexWrap:'wrap', paddingBottom:8 }}>
            <button onClick={onResults} style={{ background:'#ffffff', border:'1.5px solid #dde3ed', color:'#1a2233', padding:'10px 20px', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:500, boxShadow:'0 1px 4px rgba(26,42,74,0.06)' }}>🏆 Candidate Results</button>
            <button onClick={onEssays}  style={{ background:'#ffffff', border:'1.5px solid #dde3ed', color:'#1a2233', padding:'10px 20px', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:500, boxShadow:'0 1px 4px rgba(26,42,74,0.06)' }}>📝 Published Essays</button>
          </div>
        )}

        {/* ── ACADEMY SECTION (FIX 2) ── */}
        <div style={{ marginTop:32, background:'linear-gradient(135deg,#1a2a4a,#2a3a6a)', borderRadius:16, padding:'24px 24px 20px', color:'#fff' }}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:700, marginBottom:6 }}>🏫 Live Lessons with Expert Teachers</div>
          <div style={{ fontSize:13.5, color:'rgba(255,255,255,0.75)', lineHeight:1.7, marginBottom:18, maxWidth:560 }}>
            Complement your AI writing practice with live one-to-one or group lessons. 18 specialist teachers for General English, IELTS, SAT, German and more.
          </div>
          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            <button onClick={onAcademy}
              style={{ background:'#25d366', color:'#fff', border:'none', padding:'11px 22px', borderRadius:9, fontSize:13.5, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", display:'flex', alignItems:'center', gap:7, boxShadow:'0 2px 10px rgba(37,211,102,0.35)' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Book a Trial Lesson
            </button>
            <button onClick={onAcademy}
              style={{ background:'rgba(255,255,255,0.12)', color:'#fff', border:'1.5px solid rgba(255,255,255,0.25)', padding:'11px 22px', borderRadius:9, fontSize:13.5, fontWeight:600, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
              View All Courses →
            </button>
          </div>
        </div>
      </div>

      <Footer branding={branding}/>
    </div>
  );
}
